import Link from 'next/link';
import styled, { css } from 'styled-components';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Element, Section } from 'src/constants/footfall';
import { showSignupModal } from 'src/modules/shared/modal/actions';
import i18n from 'src/utils/translate';
import { useDispatch, useSelector } from 'src/store/store';
import { SearchType } from 'src/constants/types';
import Icon from 'src/components/icon';
import { titleMedium } from 'styles-js/mixins/typography';

type Props = {
  searchType: SearchType;
  communitySlug?: string;
  isMemberOfCommunity?: boolean;
  isNoQuery?: boolean;
}

export default function SearchTips({
  communitySlug = null,
  isMemberOfCommunity = false,
  isNoQuery = false,
  searchType,
}: Props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(({ context }) => context.currentUser);

  const onClick = (e) => {
    if (!currentUser) {
      e.preventDefault();
      dispatch(showSignupModal());
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: {
          section: Section.SEARCH_TIPS,
          element: Element.LINK,
        },
      });
    } else {
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'search-ask',
        clickMetadata: { section: Section.Search },
      });
    }
  };

  const asLink = communitySlug ? `/${communitySlug}/posts/write` : '/write';
  const href = communitySlug ? `/community/write?communitySlug=${communitySlug}` : '/community/write';

  return (
    <Wrapper $isNoQuery={isNoQuery}>
      <Title>
        {i18n.t('Search tips')}
      </Title>
      <Tip>{i18n.t('Try searching with other similar words')}</Tip>
      <Tip>{i18n.t('Double check your spelling')}</Tip>

      {searchType === 'communities' && (
        <>
          <Tip>{i18n.t(`Search results don't show private communities`)}</Tip>
          <Tip>{i18n.t(`Still can't find what you're looking for? Speak to a relevant organisation about starting a HealthUnlocked community`)}</Tip>
        </>
      )}

      {searchType === 'members' && (
        <Tip>{i18n.t('Search results only include visible profile information')}</Tip>
      )}

      {searchType === 'posts' && (
        <>
          {!isMemberOfCommunity && <Tip>{i18n.t('Search results only include public posts')}</Tip>}
          <Tip>
            {i18n.t(`If you still can't find what you're looking for, try `)}
            <Link as={asLink} href={href} onClick={onClick}>
              {i18n.t('asking your community')}
            </Link>
          </Tip>
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div<{ $isNoQuery: boolean }>`
  border: 1px solid ${({ theme }) => theme.colorGreyLight};
  margin: 20px 0;
  padding: 15px;

  @media(${({ theme }) => theme.underScreenSmall}) {
    border: none;
  }

  ${({ $isNoQuery }) => $isNoQuery && css`
    border: none;
  `};
`;

const Title = styled.div`
  ${titleMedium}
  @media(${({ theme }) => theme.underScreenSmall}) {
    border-bottom: 1px solid ${({ theme }) => theme.colorGreyLight};
    padding-bottom: 10px;
  }
`;

function Tip({ children }) {
  return (
    <StyledTip>
      <StyledIcon colour="colorBlue" icon="search" />
      <span>{children}</span>
    </StyledTip>
  );
}

const StyledTip = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 20px;
`;

const StyledIcon = styled(Icon)`
  border-radius: 50%;
  background: ${({ theme }) => theme.colorGreyLight};
  width: 33px;
  height: 33px;
`;
